import { ArrowLeft, Braces, Check } from "lucide-react";
import { useMemo } from "react";

import { getRequestVolumeSignalColor } from "./useRequestVolumeTailChartOptions";

import { EntityIcon } from "@/components/ui/icons";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";
import { EntityType } from "@/queries";
import type { GetRequestVolumeTailResponse } from "@/queries/signals";

interface SignalBreakdownProps {
  data: GetRequestVolumeTailResponse | undefined;
  totalRequests: number;
  selectedSignalId?: number | null;
  scopeName?: string;
  isDirectScope?: boolean;
  onClearScope?: () => void;
  onSignalClick?: (signalId: number, signalName: string) => void;
  onClearSignal?: () => void;
}

const getRate = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 100) : 0;

const SignalBreakdown = ({
  data,
  totalRequests,
  selectedSignalId = null,
  scopeName,
  isDirectScope = false,
  onClearScope,
  onSignalClick,
  onClearSignal,
}: SignalBreakdownProps) => {
  const signals = useMemo(() => {
    const counts = new Map<number, number>();

    for (const bucket of data?.buckets ?? []) {
      for (const signalCount of bucket.signal_counts) {
        counts.set(
          signalCount.signal_id,
          (counts.get(signalCount.signal_id) ?? 0) + signalCount.count,
        );
      }
    }

    return (data?.signals ?? [])
      .map(signal => ({
        ...signal,
        count: counts.get(signal.id) ?? 0,
      }))
      .filter(signal => signal.count > 0)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [data]);

  const hasConfiguredSignals = (data?.signals.length ?? 0) > 0;

  let content = (
    <div
      className="scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden pr-1"
      style={{ scrollbarGutter: "stable" }}
    >
      {signals.map(signal => {
        const selected = signal.id === selectedSignalId;
        return (
          <button
            key={signal.id}
            type="button"
            className={cn(
              "group flex w-full min-w-0 flex-col rounded-md px-2 py-1.5 text-left transition-colors disabled:cursor-default",
              selected ? "bg-blue-50/70 ring-1 ring-inset ring-blue-100" : "hover:bg-gray-50",
            )}
            onClick={() => {
              if (selected) {
                onClearSignal?.();
              } else {
                onSignalClick?.(signal.id, signal.name);
              }
            }}
            disabled={!onSignalClick && !onClearSignal}
            aria-pressed={selected}
            title={selected ? `Stop filtering by ${signal.name}` : `Filter chart by ${signal.name}`}
          >
            <span className="flex w-full min-w-0 items-center gap-2">
              <span
                className="h-2 w-2 shrink-0 rounded-sm"
                style={{ backgroundColor: getRequestVolumeSignalColor(signal.id) }}
              />
              <OverflowDisplayTooltip content={signal.name} className="min-w-0 flex-1">
                <span
                  className={
                    selected
                      ? "block truncate text-[11px] font-medium text-gray-900"
                      : "block truncate text-[11px] text-gray-500 group-hover:text-gray-800"
                  }
                >
                  {signal.name}
                </span>
              </OverflowDisplayTooltip>
              <span className="shrink-0 text-[10px] tabular-nums text-gray-400">
                {signal.count}
              </span>
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
                  selected
                    ? "border-blue-500 bg-blue-500 text-white"
                    : "border-gray-200 bg-white text-transparent group-hover:border-gray-300",
                )}
                aria-hidden="true"
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
            </span>
            <span
              className={cn(
                "mt-0.5 pl-4 text-[10px]",
                selected ? "text-blue-600" : "text-gray-400",
              )}
            >
              {selected
                ? "Filtering chart"
                : `${getRate(signal.count, totalRequests)}% of requests in scope`}
            </span>
          </button>
        );
      })}
    </div>
  );

  if (!hasConfiguredSignals) {
    content = (
      <div className="flex min-h-0 flex-1 items-center px-2 text-[11px] leading-relaxed text-gray-400">
        No Signals configured yet. Create one to start matching request activity.
      </div>
    );
  } else if (totalRequests === 0) {
    content = (
      <div className="flex min-h-0 flex-1 items-center px-2 text-[11px] text-gray-400">
        No Signal activity yet
      </div>
    );
  } else if (signals.length === 0) {
    content = (
      <div className="flex min-h-0 flex-1 items-center px-2 text-[11px] leading-relaxed text-gray-400">
        Signals are configured, but none have matched in this scope yet.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
      <div className="mb-2 shrink-0 space-y-2">
        {scopeName ? (
          <>
            <button
              type="button"
              className="flex items-center gap-1 text-[10px] font-medium text-gray-400 transition-colors hover:text-gray-700"
              onClick={onClearScope}
            >
              <ArrowLeft className="h-3 w-3" />
              All Templates
            </button>
            <div className="flex min-w-0 items-center gap-2 rounded-md bg-gray-50 px-2 py-1.5">
              {isDirectScope ? (
                <Braces className="h-3 w-3 text-gray-400" />
              ) : (
                <EntityIcon type={EntityType.PROMPT} size="xs" />
              )}
              <OverflowDisplayTooltip content={scopeName} className="min-w-0 flex-1">
                <span className="block truncate text-[11px] font-medium text-gray-700">
                  {scopeName}
                </span>
              </OverflowDisplayTooltip>
            </div>
          </>
        ) : null}
        <div>
          <h3 className="text-xs font-medium text-gray-700">Signals</h3>
          <p className="mt-0.5 text-[10px] text-gray-400">
            {hasConfiguredSignals
              ? "Select one to filter the chart"
              : "Create Signals to start seeing match breakdowns"}
          </p>
        </div>
      </div>

      {content}
    </div>
  );
};

export default SignalBreakdown;
